import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  DollarSign,
  Clock,
  Loader2,
  Menu,
  Info,
  Building2,
  Calendar,
  ArrowUpRight,
  CreditCard,
  Wallet,
} from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import AuthenticatedNavbar from "../components/AuthenticatedNavbar";
import DashboardSidebar from "../components/DashboardSidebar";
import { useSelector } from "react-redux";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Earnings = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    network: "",
    phoneNumber: "",
    accountName: "",
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('earnings');
  const [selectedYear, setSelectedYear] = useState('2024');
  const [payoutFilter, setPayoutFilter] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');

  // Mock data for the new design
  const monthlyEarningsData = [
    { month: 'Jan', amount: 0 },
    { month: 'Feb', amount: 0 },
    { month: 'Mar', amount: 0 },
    { month: 'Apr', amount: 0 },
    { month: 'May', amount: 0 },
    { month: 'Jun', amount: 0 },
    { month: 'Jul', amount: 0 },
    { month: 'Aug', amount: 0 },
    { month: 'Sep', amount: 0 },
    { month: 'Oct', amount: 0 },
    { month: 'Nov', amount: 0 },
    { month: 'Dec', amount: 0 },
  ];


  useEffect(() => {
    // Simulate API call
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, []);


  const confirmPayoutRequest = () => {
    // Validate form
    if (
      !payoutForm.network ||
      !payoutForm.phoneNumber ||
      !payoutForm.accountName
    ) {
      toast.error("Please fill in all fields");
      return;
    }

    // Validate phone number format
    const phoneRegex = /^(\+233|0)[0-9]{9}$/;
    if (!phoneRegex.test(payoutForm.phoneNumber)) {
      toast.error("Please enter a valid Ghanaian phone number");
      return;
    }

    // Simulate API call
    setIsLoading(true);
    setTimeout(() => {
      toast.success("Payout request submitted successfully!");
      setShowPayoutModal(false);
      setIsLoading(false);
      // Reset form
      setPayoutForm({
        network: "",
        phoneNumber: "",
        accountName: "",
      });
    }, 2000);
  };

  const handleFormChange = (field, value) => {
    setPayoutForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <DashboardSidebar 
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        user={user}
      />

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-80' : ''}`}>
        <AuthenticatedNavbar />

        <div className="px-4 py-20 mx-auto max-w-8xl sm:px-6 lg:px-8">
          {/* Sidebar Toggle Button */}
          {!sidebarOpen && (
            <div className="mb-6">
              <button
                onClick={() => setSidebarOpen(true)}
                className="flex items-center gap-2 px-4 py-2 text-[#F2640F] bg-[#F2640F]/10 rounded-lg border border-[#F2640F]/20 hover:bg-[#F2640F]/20 transition-colors"
              >
                <Menu className="w-5 h-5" />
                Open Sidebar
              </button>
            </div>
          )}
        {/* Header */}
        <div className="mb-8">
          <div className="flex gap-3 items-center mb-2">
            <h1 className="text-3xl font-bold text-gray-900">
              Earnings Dashboard
            </h1>
            <span className="px-3 py-1 text-sm font-medium text-white bg-gradient-to-r from-[#F2640F] to-[#FF0000] rounded-full">
              Coming Soon
            </span>
          </div>
          <p className="mt-2 text-gray-600">
            Track your earnings, manage payouts, and monitor your performance
          </p>
        </div>

        {/* Top-Level Summary Cards */}
        <div className="grid grid-cols-1 gap-4 mb-8 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {/* Earnings This Month */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="relative p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="mb-1 text-sm font-medium text-gray-600">Earnings this month</p>
                <p className="text-3xl font-bold text-gray-900">GH₵--</p>
                <div className="flex gap-1 items-center mt-2">
                  <ArrowUpRight className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-600">+0% vs some lost week</span>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-gray-600" />
                </div>
                <Info className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </motion.div>

          {/* Pending Earnings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="relative p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="mb-1 text-sm font-medium text-gray-600">Pending Earnings</p>
                <p className="text-3xl font-bold text-gray-900">GH₵--</p>
              </div>
              <div className="flex gap-2">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Clock className="w-5 h-5 text-gray-600" />
                </div>
                <Info className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </motion.div>

          {/* Total Paid Earnings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="relative p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="mb-1 text-sm font-medium text-gray-600">Total Paid Earnings</p>
                <p className="text-3xl font-bold text-gray-900">GH₵--</p>
              </div>
              <div className="flex gap-2">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <CreditCard className="w-5 h-5 text-gray-600" />
                </div>
                <Info className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </motion.div>

          {/* Unpaid Earnings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="relative p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="mb-1 text-sm font-medium text-gray-600">Unpaid Earnings</p>
                <p className="text-3xl font-bold text-gray-900">GH₵--</p>
                <p className="mt-2 text-xs text-gray-500">Auto withdraw when GH₵500</p>
              </div>
              <div className="flex gap-2">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Wallet className="w-5 h-5 text-gray-600" />
                </div>
                <Info className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Left Column - Earning Metrics */}
          <div className="space-y-8 lg:col-span-2">
            {/* Current Earning Balance */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
            >
              <h2 className="mb-4 text-xl font-semibold text-gray-900">Current Earning Balance</h2>
              <div className="mb-6">
                <p className="mb-2 text-sm text-gray-600">Transferable Amount</p>
                <p className="text-3xl font-bold text-gray-900">GH₵--</p>
              </div>
              
              {/* Set up payout account card */}
              <div className="p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                <div className="flex gap-4 items-start">
                  <div className="p-3 bg-white rounded-lg shadow-sm">
                    <Building2 className="w-6 h-6 text-[#F2640F]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="mb-2 text-lg font-semibold text-gray-900">
                      Set up your payout account to get paid
                    </h3>
                    <p className="mb-4 text-gray-600">
                      To get paid, you'll need to set up your payout account. It should only take a few minutes.
                    </p>
                    <button 
                      onClick={() => navigate("/payout-setup")}
                      className="px-6 py-3 bg-gradient-to-r from-[#F2640F] to-[#FF0000] text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-200"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </div>
              
              <p className="mt-4 text-sm text-gray-500">
                To add additional payout methods, please visit your account settings.
              </p>
              
              <button className="w-full mt-6 px-6 py-3 bg-gradient-to-r from-[#F2640F] to-[#FF0000] text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-200">
                Transfer Balance
              </button>
            </motion.div>

            {/* Monthly Earnings Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Monthly Earnings</h2>
                <div className="flex gap-2 items-center">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F2640F] focus:border-transparent"
                  >
                    <option value="2024">2024</option>
                    <option value="2023">2023</option>
                    <option value="2022">2022</option>
                  </select>
                </div>
              </div>
              
              {/* Chart */}
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyEarningsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="month" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      domain={[0, 20000]}
                      tickFormatter={(value) => `GH₵${(value / 1000)}k`}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                      formatter={(value) => [`GH₵${value}`, 'Earnings']}
                    />
                    <Bar 
                      dataKey="amount" 
                      fill="url(#gradient)"
                      radius={[4, 4, 0, 0]}
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#F2640F" />
                        <stop offset="100%" stopColor="#FF0000" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Payout History */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Payout History</h2>
                <div className="flex gap-2 items-center">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <select
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F2640F] focus:border-transparent"
                  >
                    <option value="">Select date</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="year">This Year</option>
                  </select>
                </div>
              </div>
              
              {/* Filter Tabs */}
              <div className="flex flex-wrap gap-2 mb-6">
                <button
                  onClick={() => setPayoutFilter('all')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    payoutFilter === 'all'
                      ? 'bg-[#F2640F] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setPayoutFilter('deposits')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    payoutFilter === 'deposits'
                      ? 'bg-[#F2640F] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Deposits
                </button>
                <button
                  onClick={() => setPayoutFilter('transfers')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    payoutFilter === 'transfers'
                      ? 'bg-[#F2640F] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Transfers
                </button>
              </div>
              
              {/* Payout History Content */}
              <div className="min-h-[400px] flex items-center justify-center">
                <div className="text-center">
                  <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full">
                    <CreditCard className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500">No payout history yet</p>
                  <p className="mt-1 text-sm text-gray-400">Your payout transactions will appear here</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        </div>
      </div>

      {/* Payout Request Modal */}
      {showPayoutModal && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
                     <motion.div
             initial={{ opacity: 0, scale: 0.9 }}
             animate={{ opacity: 1, scale: 1 }}
             className="p-4 mx-4 w-full max-w-sm bg-white rounded-2xl sm:p-6 sm:max-w-md"
           >
            <h3 className="mb-4 text-lg font-bold text-gray-900">
              Request Mobile Money Payout
            </h3>
            <p className="mb-6 text-sm text-gray-600">
              Enter your mobile money details to receive your payout securely.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block mb-3 text-sm font-medium text-gray-700">
                  Mobile Money Network
                </label>
                                 <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  <label
                                         className={`relative cursor-pointer rounded-lg border-2 p-2 sm:p-4 transition-all duration-200 ${
                       payoutForm.network === "mtn"
                         ? "border-[#F2640F] bg-[#F2640F]/5"
                         : "border-gray-200 hover:border-gray-300"
                     }`}
                  >
                    <input
                      type="radio"
                      name="network"
                      value="mtn"
                      checked={payoutForm.network === "mtn"}
                      onChange={(e) =>
                        handleFormChange("network", e.target.value)
                      }
                      className="sr-only"
                    />
                                         <div className="text-center">
                       <div className="mb-1 text-sm font-semibold text-yellow-600 sm:mb-2 sm:text-lg">
                         MTN
                       </div>
                       <div className="text-xs text-gray-600">Mobile Money</div>
                     </div>
                                         {payoutForm.network === "mtn" && (
                       <div className="absolute top-1 right-1 sm:top-2 sm:right-2 w-3 h-3 sm:w-4 sm:h-4 bg-[#F2640F] rounded-full flex items-center justify-center">
                         <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>
                       </div>
                     )}
                  </label>

                                     <label
                     className={`relative cursor-pointer rounded-lg border-2 p-2 sm:p-4 transition-all duration-200 ${
                       payoutForm.network === "telecel"
                         ? "border-[#F2640F] bg-[#F2640F]/5"
                         : "border-gray-200 hover:border-gray-300"
                     }`}
                  >
                    <input
                      type="radio"
                      name="network"
                      value="telecel"
                      checked={payoutForm.network === "telecel"}
                      onChange={(e) =>
                        handleFormChange("network", e.target.value)
                      }
                      className="sr-only"
                    />
                                         <div className="text-center">
                       <div className="mb-1 text-sm font-semibold text-red-600 sm:mb-2 sm:text-lg">
                         Telecel
                       </div>
                       <div className="text-xs text-gray-600">Cash</div>
                     </div>
                                         {payoutForm.network === "telecel" && (
                       <div className="absolute top-1 right-1 sm:top-2 sm:right-2 w-3 h-3 sm:w-4 sm:h-4 bg-[#F2640F] rounded-full flex items-center justify-center">
                         <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>
                       </div>
                     )}
                  </label>

                                     <label
                     className={`relative cursor-pointer rounded-lg border-2 p-2 sm:p-4 transition-all duration-200 ${
                       payoutForm.network === "airteltigo"
                         ? "border-[#F2640F] bg-[#F2640F]/5"
                         : "border-gray-200 hover:border-gray-300"
                     }`}
                  >
                    <input
                      type="radio"
                      name="network"
                      value="airteltigo"
                      checked={payoutForm.network === "airteltigo"}
                      onChange={(e) =>
                        handleFormChange("network", e.target.value)
                      }
                      className="sr-only"
                    />
                                         <div className="text-center">
                       <div className="mb-1 text-sm font-semibold text-blue-600 sm:mb-2 sm:text-lg">
                         AirtelTigo
                       </div>
                       <div className="text-xs text-gray-600">Money</div>
                     </div>
                                         {payoutForm.network === "airteltigo" && (
                       <div className="absolute top-1 right-1 sm:top-2 sm:right-2 w-3 h-3 sm:w-4 sm:h-4 bg-[#F2640F] rounded-full flex items-center justify-center">
                         <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>
                       </div>
                     )}
                  </label>
                </div>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <input
                  type="tel"
                  placeholder="e.g., 0201234567 or +233201234567"
                  value={payoutForm.phoneNumber}
                  onChange={(e) =>
                    handleFormChange("phoneNumber", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F2640F] focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter your Ghanaian mobile number
                </p>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Account Name
                </label>
                <input
                  type="text"
                  placeholder="Name on your mobile money account"
                  value={payoutForm.accountName}
                  onChange={(e) =>
                    handleFormChange("accountName", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F2640F] focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  The name registered on your mobile money account
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowPayoutModal(false);
                  // Reset form when canceling
                  setPayoutForm({
                    network: "",
                    phoneNumber: "",
                    accountName: "",
                    earningId: null,
                  });
                }}
                className="flex-1 px-4 py-2 text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
                             <button
                 onClick={confirmPayoutRequest}
                 disabled={isLoading}
                 className="flex-1 px-3 py-2 text-white bg-[#F2640F] rounded-lg hover:bg-[#FF0000] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 sm:gap-2 text-sm sm:text-base whitespace-nowrap"
               >
                 {isLoading ? (
                   <>
                     <Loader2 className="w-3 h-3 animate-spin sm:w-4 sm:h-4" />
                     <span className="hidden sm:inline">Processing...</span>
                     <span className="sm:hidden">Processing</span>
                   </>
                 ) : (
                   <>
                     <span className="hidden sm:inline">Confirm Request</span>
                     <span className="sm:hidden">Confirm</span>
                   </>
                 )}
               </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Earnings;
